import React, { useContext } from "react";

import { Tags } from "../../../types/tags";
import { NewMemberTag } from "../../lib/discordTypes";
import useGuildTags from "./useGuildTags";

interface GuildTagsContextProps {
  guildTags: Tags[] | null;
  loading: boolean;
  selectedTag: Tags | null;
  isEditTagModalVisible: boolean;
  isDeleteTagModalVisible: boolean;
  isAddTagModalVisible: boolean | undefined;
  setSelectedTag?: React.Dispatch<any>;
  setIsEditTagModalVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDeleteTagModalVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddTagModalVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  updateTag?: any;
  deleteTag?: any;
  addTag?: (tag: NewMemberTag) => void;
}

const GuildTagsContext = React.createContext<GuildTagsContextProps>({
  guildTags: null,
  loading: true,
  selectedTag: null,
  isEditTagModalVisible: false,
  isDeleteTagModalVisible: false,
  isAddTagModalVisible: false,
});

/**
 * Purpose: A custom hook that grants access to the value object made
 * available by GuildTagsContext.Provider
 *
 * @returns - the value object provided by GuildTagsContext.Provider
 */
export function useGuildTagsContext() {
  return useContext(GuildTagsContext);
}

/**
 * This provider makes available all the data and state management callbacks needed for the Edit Tags card
 * and its Edit/Delete modal in the Discord Settings page
 *
 * @params - No params, only the children.
 * @returns - A provider with all the data and state management callbacks
 */
export function GuildTagsProvider({ children }: any) {
  const guildId: string = localStorage.getItem("guild-id") || "";

  const {
    data: guildTags,
    isLoading: loading,
    selectedTag,
    isEditTagModalVisible,
    isDeleteTagModalVisible,
    setSelectedTag,
    setIsEditTagModalVisible,
    setIsDeleteTagModalVisible,
    updateTag,
    deleteTag,
    isAddTagModalVisible,
    setIsAddTagModalVisible,
    addTag,
  } = useGuildTags(guildId);

  return (
    <GuildTagsContext.Provider
      value={{
        guildTags,
        loading,
        selectedTag,
        isEditTagModalVisible,
        isAddTagModalVisible,
        isDeleteTagModalVisible,
        setSelectedTag,
        setIsEditTagModalVisible,
        setIsDeleteTagModalVisible,
        setIsAddTagModalVisible,
        updateTag,
        deleteTag,
        addTag,
      }}
    >
      {children}
    </GuildTagsContext.Provider>
  );
}
